import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  VStack, 
  HStack, 
  Heading, 
  Text, 
  Button, 
  Input,
  Flex,
  Spinner
} from '@chakra-ui/react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config.js';
import authService from '../admin/authService.js';

const Admin = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [password, setPassword] = useState('');
  const [showLogin, setShowLogin] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    if (authService.isAuthenticated()) {
      setShowLogin(false);
      fetchLeads();
      setTokenInfo(authService.getTokenInfo());
    }
  }, []);

  // Vérifier l'expiration du token toutes les minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (!authService.isAuthenticated()) {
        setShowLogin(true);
        setError('Session expirée. Veuillez vous reconnecter.');
      }
    }, 60000); // Vérifier toutes les minutes

    return () => clearInterval(interval);
  }, []);

  const handleLogin = () => {
    const result = authService.login(password);
    
    if (result.success) {
      setShowLogin(false);
      setTokenInfo(authService.getTokenInfo());
      fetchLeads();
    } else {
      setError(result.error);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setShowLogin(true);
    setTokenInfo(null);
    setLeads([]);
    setFilteredLeads([]);
    setPassword('');
    setError(null);
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const leadsRef = collection(db, 'contacts');
      const q = query(leadsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const leadsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setLeads(leadsData);
      setFilteredLeads(leadsData);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la récupération des leads:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    const filtered = leads.filter(lead => 
      lead.firstName?.toLowerCase().includes(term) ||
      lead.lastName?.toLowerCase().includes(term) ||
      lead.email?.toLowerCase().includes(term) ||
      lead.phone?.includes(term) ||
      lead.service?.toLowerCase().includes(term)
    );
    
    setFilteredLeads(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Prénom', 'Nom', 'Email', 'Téléphone', 'Service', 'Source'];
    const csvContent = [
      headers.join(','),
      ...filteredLeads.map(lead => [
        new Date(lead.timestamp?.toDate()).toLocaleDateString('fr-FR'),
        lead.firstName || '',
        lead.lastName || '',
        lead.email || '',
        lead.phone || '',
        lead.service || '',
        lead.source || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.toDate()).toLocaleString('fr-FR');
  };

  if (showLogin) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" p={8}>
        <Container maxW="400px" py={20}>
          <VStack spacing={8} p={10} bg="white" borderRadius="3xl" boxShadow="0 20px 60px rgba(0,0,0,0.1)">
            <VStack spacing={4}>
              <Box
                w="80px"
                h="80px"
                bg="linear-gradient(135deg, #0053b3, #ef3340)"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 10px 30px rgba(0,83,179,0.3)"
              >
                <Text fontSize="2xl">🔐</Text>
              </Box>
              <Heading size="xl" color="#0053b3" textAlign="center" fontWeight={800}>
                Accès Administrateur
              </Heading>
              <Text color="gray.600" textAlign="center" fontSize="sm">
                Connectez-vous pour accéder au tableau de bord
              </Text>
            </VStack>
            
            <VStack spacing={6} w="100%">
              <Input
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                size="lg"
                borderRadius="xl"
                border="2px solid"
                borderColor="gray.200"
                _focus={{
                  borderColor: "#0053b3",
                  boxShadow: "0 0 0 3px rgba(0,83,179,0.1)"
                }}
              />
              {error && (
                <Box bg="red.50" p={4} borderRadius="xl" color="red.600" border="1px solid" borderColor="red.200" w="100%">
                  ❌ {error}
                </Box>
              )}
              <Button
                onClick={handleLogin}
                bg="linear-gradient(135deg, #0053b3, #ef3340)"
                color="white"
                w="100%"
                size="lg"
                borderRadius="xl"
                fontWeight={700}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 25px rgba(0,83,179,0.3)"
                }}
                transition="all 0.3s ease"
              >
                Se connecter
              </Button>
            </VStack>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" p={8}>
      <Container maxW="1600px">
        <VStack spacing={6} align="stretch">
          {/* En-tête simple */}
          <Box bg="white" borderRadius="3xl" p={8} boxShadow="0 20px 60px rgba(0,0,0,0.1)">
            <Flex justify="space-between" align="center" wrap="wrap" gap={6}>
              <VStack align="start" spacing={3}>
                <HStack spacing={4} align="center">
                  <Heading size="2xl" color="#0053b3" fontWeight={800}>
                    📊 Tableau de bord
                  </Heading>
                  <Button
                    onClick={handleLogout}
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    borderRadius="full"
                  >
                    🚪 Déconnexion
                  </Button>
                </HStack>
                <HStack spacing={4}>
                  <Box
                    bg="linear-gradient(135deg, #0053b3, #ef3340)"
                    color="white"
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight={700}
                  >
                    {filteredLeads.length} lead{filteredLeads.length > 1 ? 's' : ''}
                  </Box>
                  <Text color="gray.600" fontSize="sm">
                    Dernière mise à jour : {new Date().toLocaleTimeString('fr-FR')}
                  </Text>
                  {tokenInfo && (
                    <Text color="green.600" fontSize="xs" fontWeight={600}>
                      🔒 Session valide jusqu'à {tokenInfo.expiresAt.toLocaleTimeString('fr-FR')}
                    </Text>
                  )}
                </HStack>
              </VStack>
              <Button
                onClick={exportToCSV}
                bg="linear-gradient(135deg, #0053b3, #ef3340)"
                color="white"
                size="lg"
                px={8}
                py={6}
                borderRadius="xl"
                fontWeight={700}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 25px rgba(0,83,179,0.3)"
                }}
                transition="all 0.3s ease"
              >
                📥 Exporter CSV
              </Button>
            </Flex>
          </Box>

          {/* Barre de recherche simple */}
          <Box bg="white" borderRadius="2xl" p={6} boxShadow="0 10px 30px rgba(0,0,0,0.1)">
            <Input
              placeholder="🔍 Rechercher par nom, email, téléphone..."
              value={searchTerm}
              onChange={handleSearch}
              size="lg"
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor="gray.200"
              _focus={{
                borderColor: "#0053b3",
                boxShadow: "0 0 0 3px rgba(0,83,179,0.1)"
              }}
              _placeholder={{ color: "gray.400" }}
            />
          </Box>

          {/* Liste des leads */}
          {loading ? (
            <Flex justify="center" py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color="#0053b3" thickness="4px" />
                <Text color="gray.600" fontSize="lg">Chargement des leads...</Text>
              </VStack>
            </Flex>
          ) : error ? (
            <Box
              bg="red.50"
              p={8}
              borderRadius="2xl"
              color="red.600"
              border="1px solid"
              borderColor="red.200"
              textAlign="center"
            >
              <Text fontSize="xl" mb={2}>❌ Erreur</Text>
              <Text>{error}</Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {filteredLeads.map((lead, index) => (
                <Box
                  key={lead.id}
                  bg="white"
                  borderRadius="2xl"
                  p={6}
                  boxShadow="0 10px 30px rgba(0,0,0,0.1)"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
                  }}
                  transition="all 0.3s ease"
                  position="relative"
                  overflow="hidden"
                >
                  {/* Indicateur de statut */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    h="4px"
                    bg="linear-gradient(90deg, #0053b3, #ef3340)"
                  />
                  
                  <VStack align="start" spacing={4}>
                    <HStack justify="space-between" w="100%">
                      <HStack spacing={4}>
                        <Box
                          w="50px"
                          h="50px"
                          bg="linear-gradient(135deg, #0053b3, #ef3340)"
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color="white"
                          fontWeight={700}
                          fontSize="lg"
                        >
                          {lead.firstName?.charAt(0)}{lead.lastName?.charAt(0)}
                        </Box>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight={800} fontSize="xl" color="#0053b3">
                            {lead.firstName} {lead.lastName}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            Lead #{index + 1}
                          </Text>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.500" bg="gray.100" px={3} py={1} borderRadius="full">
                        {formatDate(lead.timestamp)}
                      </Text>
                    </HStack>
                    
                    <HStack spacing={6} wrap="wrap" w="100%">
                      <VStack align="start" spacing={1} minW="200px">
                        <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">
                          📧 Email
                        </Text>
                        <Text fontSize="md" color="gray.800" fontWeight={500}>
                          {lead.email}
                        </Text>
                      </VStack>
                      
                      <VStack align="start" spacing={1} minW="150px">
                        <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">
                          📞 Téléphone
                        </Text>
                        <Text fontSize="md" color="gray.800" fontWeight={500}>
                          {lead.phone}
                        </Text>
                      </VStack>
                    </HStack>
                    
                    <HStack spacing={6} wrap="wrap" w="100%">
                      <VStack align="start" spacing={1} minW="200px">
                        <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">
                          🛠️ Service
                        </Text>
                        <Box
                          bg="linear-gradient(135deg, #0053b3, #ef3340)"
                          color="white"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="sm"
                          fontWeight={600}
                        >
                          {lead.service}
                        </Box>
                      </VStack>
                      
                      <VStack align="start" spacing={1} minW="150px">
                        <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">
                          📍 Source
                        </Text>
                        <Box
                          bg="green.100"
                          color="green.800"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="sm"
                          fontWeight={600}
                        >
                          {lead.source}
                        </Box>
                      </VStack>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}

          {filteredLeads.length === 0 && !loading && (
            <Box
              bg="white"
              borderRadius="2xl"
              p={12}
              textAlign="center"
              boxShadow="0 10px 30px rgba(0,0,0,0.1)"
            >
              <Text fontSize="xl" color="gray.500" mb={2}>🔍</Text>
              <Text fontSize="lg" color="gray.600" fontWeight={600}>
                Aucun lead trouvé
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Essayez de modifier votre recherche
              </Text>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default Admin;